import React from 'react';
import { Link } from 'react-router-dom';
import type { LabResultRecord } from '../../types/labs';
import { ChevronDownIcon } from '../layout/memberPrimitives';
import { resolveBiomarker } from '../../data/biomarkerCatalogue';
import { computeBiomarkerStatus, type BiomarkerStatusResult } from '../../utils/biomarkerStatus';
import { buildBiomarkerHistory } from '../../utils/biomarkerHistory';
import InfoTip from '../ui/InfoTip';
import BiomarkerLearnMore from './BiomarkerLearnMore';
import BiomarkerRangeBar from './BiomarkerRangeBar';
import BiomarkerTrend from './BiomarkerTrend';
import BiomarkerStatusPill from './BiomarkerStatusPill';

interface Props {
  result: LabResultRecord;
  allResults: LabResultRecord[];
  sex: string | null;
  showDraftCopy: boolean;
  onEdit: (result: LabResultRecord) => void;
  formatDate: (value?: unknown) => string;
  /**
   * Whether this reading came back on the member's most recent draw. The list
   * shows the newest reading per marker across every draw on file, so without
   * this a member cannot tell which rows are the test they just had — which was
   * the question that sent them to this page.
   */
  isFromLatestDraw?: boolean;
}

/** Trim float noise from a unit conversion without hiding genuine precision. */
function formatCanonical(value: number): string {
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 100) / 100;
  return String(rounded);
}

/**
 * Last-resort reference line for a record `computeBiomarkerStatus` could not
 * judge — an uncatalogued marker, or a non-numeric value like "HAEMOLYSED"
 * against a free-text range. Numeric records with a printed range no longer
 * reach here: the status engine now falls back to the lab's range itself and
 * draws a real bar, so this handles only what is left.
 */
function StoredRange({ result }: { result: LabResultRecord }) {
  const ref = result.referenceRange || {};
  const low = typeof ref.low === 'number' ? ref.low : null;
  const high = typeof ref.high === 'number' ? ref.high : null;
  const rangeText = low != null && high != null
    ? `${low}–${high}`
    : high != null ? `<${high}` : low != null ? `>${low}` : (ref.text || null);
  if (!rangeText) return null;
  return (
    <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
      {`Reference ${rangeText}${result.unit ? ` ${result.unit}` : ''}`}
    </p>
  );
}

/** Clock time of the draw, shown only when one was actually reported. */
function drawTime(result: LabResultRecord): string | null {
  const stamp = result.collectedAt || result.observedAt;
  const precision = result.collectedAt ? result.collectedAtPrecision : result.observedAtPrecision;
  // Absent precision means a legacy date-only record. Never infer a time from a
  // bare date: every one of those would render "00:00" and read as a midnight
  // blood draw that never happened.
  if (!stamp || precision !== 'time') return null;
  const parsed = new Date(stamp);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/**
 * When the sample was taken and whether the member had fasted.
 *
 * Both change how a result should be read — the engine applies a fasting
 * triglyceride threshold, and its own guidance asks for a morning testosterone
 * and a rested prolactin — and neither was shown anywhere. Unreported fasting
 * stays silent rather than claiming a state we were never told.
 */
function SampleContext({ result }: { result: LabResultRecord }) {
  const time = drawTime(result);
  const fasting = result.fastingStatus;
  const derived = result.derived ? result.derivedEquation || 'calculated by Melio' : null;
  const parts = [
    time ? `Taken at ${time}` : null,
    fasting === true ? 'fasting' : fasting === false ? 'not fasting' : null,
    derived,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <p className="text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
      {parts.join(' · ')}
    </p>
  );
}

/*
 * Range provenance — whose numbers judged this result — used to be a separate
 * line here. It now rides on the range caption inside BiomarkerRangeBar, so
 * every surface that draws a bar states it, and the row no longer prints two
 * grey lines that both end in the word "range".
 */

/**
 * The safety net. Melio's range governs, by product decision — but it must never
 * be the reason a member fails to learn that their own laboratory flagged a
 * result. This fires only in the one direction that can hide something: we say
 * in-range, the lab said otherwise. The reverse (we flag, the lab did not) is
 * the product working as intended and needs no disclaimer.
 */
function LabDisagreementNote({ status }: { status: BiomarkerStatusResult }) {
  if (status.status !== 'in_range' || !status.labFlag) return null;
  const worded = status.labFlag === 'abnormal' ? 'abnormal' : status.labFlag;
  return (
    <p className="text-xs mt-1.5" style={{ color: 'var(--m-attention)' }}>
      Your laboratory flagged this result as {worded}. It sits inside Melio&rsquo;s range —
      your care team reviews both.
    </p>
  );
}

export default function BiomarkerEntryRow({
  result,
  allResults,
  sex,
  showDraftCopy,
  onEdit,
  formatDate,
  isFromLatestDraw = false,
}: Props) {
  const [expanded, setExpanded] = React.useState(false);
  const meta = resolveBiomarker({ biomarkerKey: result.biomarkerKey, biomarker: result.biomarker });
  const status = meta ? computeBiomarkerStatus(result, meta, sex) : null;
  const showExplanation = Boolean(meta && (meta.copyStatus === 'approved' || showDraftCopy));
  const history = meta ? buildBiomarkerHistory(allResults, meta, sex) : [];

  // Every explanation in the catalogue is currently `draft` and therefore
  // withheld in production (see docs/clinical/BIOMARKER_COPY_SIGNOFF.md), which
  // left a member reading "ApoB 1.4 mmol/L [High]" with nothing telling them
  // what ApoB is or whether to worry. We cannot ship unapproved clinical copy,
  // but we can stop the silence: this line is deliberately non-clinical — it
  // makes no claim about the marker — and it gives the member somewhere to go.
  const showSafeHarbour = !showExplanation;
  /**
   * The deep-dive is offered only where there is one to show, and only through
   * the same copy gate as the short explanation — an unapproved essay must not
   * reach a member any more than an unapproved sentence.
   */
  const [learnMoreOpen, setLearnMoreOpen] = React.useState(false);
  const canLearnMore = Boolean(showExplanation && meta?.longExplanation);

  // Only when a bar is actually drawn (so there is a scale to reconcile with)
  // and the reported unit differs from the one the scale is in.
  //
  // Restricted to a Melio-sourced range. On the lab fallback the value is NOT
  // converted — the lab's range is already in the record's own unit — so
  // `canonicalValue` is the raw number, and labelling it with `meta.unit` would
  // assert a conversion that never happened (folate 11.6 nmol/L relabelled
  // "11.6 µg/L", which is a different concentration entirely).
  const normalise = (unit?: string | null) =>
    (unit || '').toString().toLowerCase().replace(/\s+/g, '').replace(/[μµ]/g, 'u');
  // A censored bound keeps its symbol through the conversion: "< 7.00 g/L"
  // converted must read "< 700 mg/dL", never "700 mg/dL".
  const censoredPrefix = status?.censored ? (status.censored.direction === 'below' ? '<' : '>') : '';
  const convertedLabel =
    meta && status?.hasBar && status.rangeSource === 'melio' && status.canonicalValue != null
      && normalise(result.unit) !== normalise(meta.unit)
      ? `${censoredPrefix}${formatCanonical(status.canonicalValue)} ${meta.unit}`
      : null;

  const toggle = () => setExpanded((v) => !v);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      className="w-full text-left rounded-2xl border p-4 transition-colors cursor-pointer hover:bg-[rgba(79, 93, 24,0.05)]"
      style={{ borderColor: 'var(--melio-border)' }}
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            {showExplanation ? (
              <InfoTip content={meta!.explanation} ariaLabel={`What is ${meta!.label}`}>
                <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{result.biomarker}</span>
              </InfoTip>
            ) : (
              <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{result.biomarker}</span>
            )}
            {/* The number is the point of the row. It used to be set at the
                same size, weight and colour as its own label. */}
            <span className="text-lg font-semibold tabular-nums" style={{ color: 'var(--m-ink)' }}>
              {result.value}
              {result.unit ? (
                <span className="text-sm font-normal ml-1" style={{ color: 'var(--melio-text-muted)' }}>
                  {result.unit}
                </span>
              ) : null}
            </span>
            {/* The range bar is drawn in the catalogue's canonical unit, but the
                value is shown as the lab reported it. When those differ the row
                read "ApoB 1.42 g/L" above a scale labelled "90 mg/dL" — two
                units in one row, and no way for a member to place themselves on
                the scale. Show the converted figure rather than silently
                rewriting the number their lab report gives them. */}
            {convertedLabel && (
              <span className="text-sm tabular-nums" style={{ color: 'var(--melio-text-muted)' }}>
                = {convertedLabel}
              </span>
            )}
            {status && <BiomarkerStatusPill status={status} />}
          </div>

          {meta && status && status.hasBar ? (
            <>
              <BiomarkerRangeBar meta={meta} status={status} />
              <LabDisagreementNote status={status} />
            </>
          ) : (
            <>
              <StoredRange result={result} />
              {/* A marker that is *expected* to have no range (the differential
                  percentages, where the lab prints none either) previously left
                  the member a bare number with no cue at all until they expanded
                  the row. Say why inline. */}
              {meta?.explanationOnly && (
                <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                  No reference range — read alongside the absolute count above.
                </p>
              )}
            </>
          )}

          <p className="text-sm mt-1" style={{ color: 'var(--melio-text-muted)' }}>
            {result.sourceDocumentName || 'No source report linked'}
          </p>
          <SampleContext result={result} />
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* "This came back on your latest draw", and nothing about the value.
              It sits with the date rather than beside the marker name: the name
              and the number are the row's reading path, and a chip wedged
              between them pushed the number — the point of the row — away from
              what it is the value of. Deliberately not a status colour, either;
              this is provenance, and colouring it would read as a verdict. */}
          {isFromLatestDraw && (
            <span
              className="text-[10px] uppercase tracking-[0.14em] rounded-full px-2 py-0.5"
              style={{ backgroundColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}
            >
              New
            </span>
          )}
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{formatDate(result.observedAt)}</p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(result);
            }}
            className="text-xs underline"
            style={{ color: 'var(--melio-text-muted)' }}
          >
            Edit
          </button>
          {/* Was the literal character ⌄ (U+2304), which rendered in the body
              serif next to a set of 1.6px-stroke SVG icons. */}
          <span
            aria-hidden
            style={{
              color: 'var(--melio-text-muted)',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 160ms ease',
              display: 'inline-flex',
            }}
          >
            <ChevronDownIcon size={16} />
          </span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--melio-border)' }} onClick={(e) => e.stopPropagation()}>
          {meta && (
            <p className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--melio-olive-deep, #4F5D18)' }}>
              {meta.domainLabel}
            </p>
          )}
          {showExplanation && meta && (
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-charcoal)' }}>
              {meta.explanation}
              {canLearnMore && (
                <button
                  type="button"
                  className="ml-2 underline text-sm"
                  style={{ color: 'var(--melio-ink)' }}
                  onClick={(event) => { event.stopPropagation(); setLearnMoreOpen(true); }}
                >
                  Learn more
                </button>
              )}
            </p>
          )}

          {learnMoreOpen && meta && (
            <BiomarkerLearnMore meta={meta} onClose={() => setLearnMoreOpen(false)} />
          )}

          {showSafeHarbour && (
            <div
              className="rounded-xl px-3 py-2.5 mb-4"
              style={{ backgroundColor: 'var(--m-surface-2)', border: '1px solid var(--m-line)' }}
            >
              <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                We haven&rsquo;t published a plain-English explanation for this marker yet. A single
                reading rarely means much on its own — your clinician reads it alongside the rest of
                your record.{' '}
                <Link to="/messages" className="underline" style={{ color: 'var(--m-accent)' }}>
                  Ask your care team about this result
                </Link>
                .
              </p>
            </div>
          )}

          {meta && status && status.hasBar && (
            <div className="mb-4">
              <BiomarkerRangeBar meta={meta} status={status} large />
              <LabDisagreementNote status={status} />
            </div>
          )}

          {meta && history.length >= 2 ? (
            <div className="mb-2">
              <p className="text-xs mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Your trend ({history.length} readings)
              </p>
              <BiomarkerTrend meta={meta} points={history} />
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
              First reading on record — your trend will appear here as you retest.
            </p>
          )}

          {result.notes && (
            <p className="text-sm mt-3" style={{ color: 'var(--melio-text-muted)' }}>{result.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}
